export type EmaDeliveryType = 'EMA' | 'EMAAC';

/**
 * Resolves which channel (EMA or EMAAC) each EMA event of an alert belongs to.
 *
 * deliveryType is stamped from the handling user, so LOG_NEW_ALERT (which has
 * no user) never carries it. For those the type is taken from the first later
 * event of the same delivery, i.e. before the next LOG_NEW_ALERT.
 *
 * A delivery with no typed events (e.g. alert cancelled before anyone handled
 * it) falls back to the alert's own deliveryType if it is the last delivery,
 * since that is where the alert ultimately went. Earlier ones fall back to EMA.
 * @param emaEvents All EMA events of the alert, in any order
 * @param alertDeliveryType Delivery type of the alert itself
 * @returns Delivery type of every event, keyed by the event object
 */
export function resolveEmaDeliveryTypes(
  emaEvents: any[],
  alertDeliveryType?: string
): Map<any, EmaDeliveryType> {
  const types = new Map<any, EmaDeliveryType>();
  if (!emaEvents?.length) {
    return types;
  }

  const sorted = [...emaEvents].sort(
    (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime()
  );

  // Walk backwards so the nearest later typed event of the delivery is known
  // when its LOG_NEW_ALERT is reached
  let deliveryFallback: EmaDeliveryType = alertDeliveryType === 'EMAAC' ? 'EMAAC' : 'EMA';
  let laterTyped: EmaDeliveryType | undefined;
  for (let i = sorted.length - 1; i >= 0; i--) {
    const event = sorted[i];
    if (event.eventType === 'LOG_NEW_ALERT') {
      types.set(event, event.deliveryType || laterTyped || deliveryFallback);
      laterTyped = undefined;
      deliveryFallback = 'EMA';
    } else {
      types.set(event, event.deliveryType || 'EMA');
      if (event.deliveryType) {
        laterTyped = event.deliveryType;
      }
    }
  }
  return types;
}
